import React, { ReactNode } from 'react';

type CardVariant = 'default' | 'outline' | 'flat';
type CardBackground = 'white' | 'gray' | 'blue' | 'transparent';
type CardPadding = 'small' | 'medium' | 'large' | 'none';
type CardRadius = 'small' | 'medium' | 'large';

export interface CardProps {
  /**
   * Card content
   */
  children: ReactNode;
  /**
   * Card variant
   */
  variant?: CardVariant;
  /**
   * Background color
   */
  background?: CardBackground;
  /**
   * Custom background color class
   */
  customBackground?: string;
  /**
   * Padding size
   */
  padding?: CardPadding;
  /**
   * Border radius
   */
  radius?: CardRadius;
  /**
   * Whether the card is clickable
   */
  clickable?: boolean;
  /**
   * Optional click handler
   */
  onClick?: () => void;
  /**
   * Whether card should take full width
   */
  fullWidth?: boolean;
  /**
   * Custom height class
   */
  height?: string;
  /**
   * Optional class name for additional styling
   */
  className?: string;
  /**
   * Optional additional shadow
   */
  shadow?: boolean;
  /**
   * Optional data attributes
   */
  [key: `data-${string}`]: string | undefined;
}

export const Card: React.FC<CardProps> = ({
  children,
  variant = 'default',
  background = 'white',
  customBackground,
  padding = 'medium',
  radius = 'medium',
  clickable = false,
  onClick,
  fullWidth = true,
  height,
  className = '',
  shadow = false,
  ...props
}) => {
  // Handle keyboard events for accessibility
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (clickable && onClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick();
    }
  };
  // Base styles
  const baseStyles = 'overflow-hidden transition-shadow';

  // Variant styles
  const variantStyles = {
    default: '',
    outline: 'border border-gray-200',
    flat: '',
  };

  // Background styles
  const backgroundStyles = {
    white: 'bg-white',
    gray: 'bg-gray-100',
    blue: 'bg-blue-600',
    transparent: 'bg-transparent',
  };

  // Padding styles
  const paddingStyles = {
    none: 'p-0',
    small: 'p-4',
    medium: 'p-6',
    large: 'p-8',
  };

  // Border radius styles
  const radiusStyles = {
    small: 'rounded',
    medium: 'rounded-lg',
    large: 'rounded-2xl',
  };

  // Clickable styles
  const clickableStyles = clickable ? 'cursor-pointer hover:shadow-md active:shadow-sm' : '';

  // Width styles
  const widthStyles = fullWidth ? 'w-full' : '';

  // Height styles
  const heightStyles = height ?? '';

  // Shadow styles
  const shadowStyles = shadow ? 'shadow-sm' : '';

  // Use custom background if provided, otherwise use predefined background
  const bgStyle = customBackground ?? backgroundStyles[background];

  // Interactive props for clickable cards
  const interactiveProps =
    clickable && onClick
      ? {
          onClick,
          onKeyDown: handleKeyDown,
          role: 'button' as const,
          tabIndex: 0,
          'aria-pressed': false,
        }
      : {};

  return (
    <div
      className={`${baseStyles} ${variantStyles[variant]} ${bgStyle} ${paddingStyles[padding]} ${radiusStyles[radius]} ${clickableStyles} ${widthStyles} ${heightStyles} ${shadowStyles} ${className}`}
      {...interactiveProps}
      {...props}
    >
      {children}
    </div>
  );
};

export default Card;
